import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

// Simple UI Components matching the TypeScript structure
const Card = ({ children, className = '', style = {}, onClick, onMouseDown }) => (
  <div 
    className={className} 
    style={{
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      ...style
    }}
    onClick={onClick}
    onMouseDown={onMouseDown}
  >
    {children}
  </div>
);

const CardHeader = ({ children, style = {} }) => (
  <div style={{
    padding: '20px',
    borderBottom: '1px solid #e5e7eb',
    ...style
  }}>
    {children}
  </div>
);

const CardTitle = ({ children, style = {} }) => (
  <h3 style={{
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
    ...style
  }}>
    {children}
  </h3>
);

const CardDescription = ({ children, style = {} }) => (
  <p style={{
    fontSize: '0.875rem',
    color: '#6b7280',
    margin: '4px 0 0 0',
    ...style
  }}>
    {children}
  </p>
);

const CardContent = ({ children, style = {} }) => (
  <div style={{
    padding: '20px',
    ...style
  }}>
    {children}
  </div>
);

const Label = ({ htmlFor, children, style = {} }) => (
  <label htmlFor={htmlFor} style={{
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
    ...style
  }}>
    {children}
  </label>
);

const Input = ({ id, type = 'text', value, onChange, placeholder, required, min, step, style = {}, onFocus, onBlur }) => (
  <input
    id={id}
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    required={required}
    min={min}
    step={step}
    onFocus={onFocus}
    onBlur={onBlur}
    style={{
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box',
      transition: 'all 0.2s',
      outline: 'none',
      ...style
    }}
  />
);

const Select = ({ id, name, value, onChange, required, children, style = {}, onFocus, onBlur }) => (
  <select
    id={id}
    name={name}
    value={value}
    onChange={onChange}
    required={required}
    onFocus={onFocus}
    onBlur={onBlur}
    style={{
      width: '100%',
      padding: '10px 14px',
      paddingRight: '40px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box',
      appearance: 'none',
      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      backgroundSize: '16px',
      transition: 'all 0.2s',
      outline: 'none',
      cursor: 'pointer',
      ...style
    }}
  >
    {children}
  </select>
);

const Textarea = ({ id, name, value, onChange, placeholder, required, rows = 4, style = {}, onFocus, onBlur }) => (
  <textarea
    id={id}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    required={required}
    rows={rows}
    onFocus={onFocus}
    onBlur={onBlur}
    style={{
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box',
      transition: 'all 0.2s',
      outline: 'none',
      resize: 'vertical',
      fontFamily: 'inherit',
      ...style
    }}
  />
);

const Switch = ({ id, checked, onCheckedChange, style = {} }) => (
  <label
    htmlFor={id}
    style={{
      position: 'relative',
      display: 'inline-block',
      width: '44px',
      height: '24px',
      cursor: 'pointer',
      ...style
    }}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onCheckedChange(e.target.checked)}
      style={{
        opacity: 0,
        width: 0,
        height: 0
      }}
    />
    <span style={{
      position: 'absolute',
      cursor: 'pointer',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: checked ? '#7c3aed' : '#cbd5e1',
      borderRadius: '24px',
      transition: '0.3s'
    }}>
      <span style={{
        position: 'absolute',
        height: '18px',
        width: '18px',
        left: checked ? '22px' : '3px',
        bottom: '3px',
        background: 'white',
        borderRadius: '50%',
        transition: '0.3s',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }}></span>
    </span>
  </label>
);

const Button = ({ children, onClick, disabled, type = 'button', variant = 'default', style = {} }) => {
  const baseStyle = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '6px',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    ...style
  };

  if (variant === 'ghost') {
    baseStyle.background = 'transparent';
    baseStyle.color = '#374151';
  } else if (variant === 'outline') {
    baseStyle.background = 'white';
    baseStyle.color = '#374151';
    baseStyle.border = '1px solid #d1d5db';
  } else {
    baseStyle.background = disabled ? '#9ca3af' : '#7c3aed';
    baseStyle.color = 'white';
    baseStyle.boxShadow = disabled ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={baseStyle}
      onMouseEnter={(e) => {
        if (!disabled && variant === 'default') {
          e.target.style.background = '#6d28d9';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && variant === 'default') {
          e.target.style.background = '#7c3aed';
        }
      }}
    >
      {children}
    </button>
  );
};

const PromoCode = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage', // 'percentage' or 'fixed'
    discountValue: '',
    maxDiscount: '',
    minPurchase: '',
    validFrom: '',
    validUntil: '',
    maxUses: '',
    currentUses: 0,
    isActive: true,
    // New fields for the updated form
    rewardType: 'Single Session',
    maxRedemptions: '',
    name: '',
    campaign: '',
    accessDuration: '',
    description: ''
  });

  // Fetch promo codes
  const fetchPromoCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/promo-codes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch promo codes');
      }

      const data = await response.json();
      if (data.success) {
        setPromoCodes(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to fetch promo codes');
      }
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      setError(err.message);
      // Set empty array on error to allow adding new codes
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const url = editingCode 
        ? `${API_BASE_URL}/api/admin/promo-codes/${editingCode.id}`
        : `${API_BASE_URL}/api/admin/promo-codes`;
      
      const method = editingCode ? 'PUT' : 'POST';

      // Convert form data to backend format - only required fields
      const requestData = {
        code: formData.code,
        rewardType: formData.rewardType || 'Single Session',
        name: formData.name,
        campaign: formData.campaign || null,
        accessDuration: formData.accessDuration || null,
        description: formData.description || null,
        maxRedemptions: formData.maxRedemptions || null
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save promo code');
      }

      const data = await response.json();
      if (data.success) {
        await fetchPromoCodes();
        setShowAddModal(false);
        setEditingCode(null);
        setSelectedCode(null);
        resetForm();
        alert('Promo code saved successfully!');
      } else {
        throw new Error(data.message || 'Failed to save promo code');
      }
    } catch (err) {
      console.error('Error saving promo code:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      maxDiscount: '',
      minPurchase: '',
      validFrom: '',
      validUntil: '',
      maxUses: '',
      currentUses: 0,
      isActive: true,
      // New fields for the updated form
      rewardType: 'Single Session',
      maxRedemptions: '',
      name: '',
      campaign: '',
      accessDuration: '',
      description: ''
    });
  };

  // Handle edit
  const handleEdit = (promoCode) => {
    setSelectedCode(promoCode);
    setEditingCode(promoCode);
    setFormData({
      code: promoCode.code,
      rewardType: promoCode.reward_type || 'Single Session',
      name: promoCode.name || '',
      campaign: promoCode.campaign || '',
      accessDuration: promoCode.access_duration?.toString() || '',
      description: promoCode.description || '',
      maxRedemptions: promoCode.max_redemptions?.toString() || '',
      // Keep old fields in state for backward compatibility but don't use them
      discountType: 'percentage',
      discountValue: '',
      maxDiscount: '',
      minPurchase: '',
      validFrom: '',
      validUntil: '',
      maxUses: '',
      currentUses: 0,
      isActive: true
    });
  };

  // Handle select code for editing
  const handleSelectCode = (promoCode) => {
    setSelectedCode(promoCode);
    setEditingCode(promoCode);
    setFormData({
      code: promoCode.code,
      rewardType: promoCode.reward_type || 'Single Session',
      name: promoCode.name || '',
      campaign: promoCode.campaign || '',
      accessDuration: promoCode.access_duration?.toString() || '',
      description: promoCode.description || '',
      maxRedemptions: promoCode.max_redemptions?.toString() || '',
      // Keep old fields in state for backward compatibility but don't use them
      discountType: 'percentage',
      discountValue: '',
      maxDiscount: '',
      minPurchase: '',
      validFrom: '',
      validUntil: '',
      maxUses: '',
      currentUses: 0,
      isActive: true
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/promo-codes/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete promo code');
      }

      const data = await response.json();
      if (data.success) {
        await fetchPromoCodes();
      } else {
        throw new Error(data.message || 'Failed to delete promo code');
      }
    } catch (err) {
      console.error('Error deleting promo code:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle active status - removed as is_active column no longer exists
  // Keeping function structure but it's no longer needed
  const handleToggleActive = async (promoCode) => {
    // This function is kept for backward compatibility but no longer functional
    // as is_active column has been removed
    console.log('Toggle active is no longer supported');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const hasChanges = selectedCode && (
    formData.code !== selectedCode.code ||
    formData.rewardType !== (selectedCode.reward_type || 'Single Session') ||
    formData.name !== (selectedCode.name || '') ||
    formData.campaign !== (selectedCode.campaign || '') ||
    formData.accessDuration !== (selectedCode.access_duration?.toString() || '') ||
    formData.description !== (selectedCode.description || '') ||
    formData.maxRedemptions !== (selectedCode.max_redemptions?.toString() || '')
  );

  return (
    <div style={{
      background: '#f9fafb',
      minHeight: '100vh',
      padding: '0'
    }}>
      {/* Header */}
      <header style={{
        borderBottom: '1px solid #e5e7eb',
        background: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '16px 24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h1 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#111827',
                margin: 0
              }}>
                Promo Code Management
              </h1>
              <p style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                margin: '4px 0 0 0'
              }}>
                Manage your promo codes and discounts
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setEditingCode(null);
                setSelectedCode(null);
                setShowAddModal(true);
              }}
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => e.target.style.background = '#6d28d9'}
              onMouseLeave={(e) => e.target.style.background = '#7c3aed'}
            >
              <i className="fas fa-plus"></i>
              Add Promo Code
            </button>
          </div>
        </div>
      </header>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '24px'
      }}>
        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            color: '#dc2626',
            marginBottom: '16px'
          }}>
            {error}
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                float: 'right',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && !showAddModal && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#7c3aed' }}></i>
          </div>
        )}

        {/* Two Column Layout */}
        {!loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '24px'
          }}>
            {/* Left Column - Promo Codes List */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              height: 'fit-content',
              maxHeight: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb'
              }}>
                <h2 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 4px 0'
                }}>
                  Promo Codes
                </h2>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  margin: 0
                }}>
                  Select a code to edit
                </p>
              </div>
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px'
              }}>
                {promoCodes.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <i className="fas fa-ticket-alt" style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}></i>
                    <p style={{ margin: 0 }}>No promo codes found</p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem' }}>Click "Add Promo Code" to create one</p>
                  </div>
                ) : (
                  promoCodes.map((promoCode) => (
                    <button
                      key={promoCode.id}
                      onClick={() => handleSelectCode(promoCode)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '12px',
                        borderRadius: '8px',
                        border: selectedCode?.id === promoCode.id ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                        background: selectedCode?.id === promoCode.id ? '#ede9fe' : 'white',
                        cursor: 'pointer',
                        marginBottom: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedCode?.id !== promoCode.id) {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedCode?.id !== promoCode.id) {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'start',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <i className="fas fa-ticket-alt" style={{ fontSize: '14px', color: '#6b7280' }}></i>
                            <span style={{
                              fontWeight: '600',
                              color: '#111827',
                              fontSize: '14px'
                            }}>
                              {promoCode.code}
                            </span>
                          </div>
                          <p style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            margin: '4px 0 0 0'
                          }}>
                            {promoCode.name || promoCode.reward_type || 'Promo Code'}
                          </p>
                        </div>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          background: '#dbeafe',
                          color: '#1e40af'
                        }}>
                          <i className="fas fa-ticket-alt" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                          {promoCode.reward_type || 'Active'}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Right Column - Editor */}
            <div>
              {selectedCode ? (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <h2 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 8px 0'
                      }}>
                        Edit Promo Code
                      </h2>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginTop: '4px'
                      }}>
                        <span>
                          <i className="fas fa-calendar" style={{ marginRight: '4px' }}></i>
                          {new Date(selectedCode.updated_at || selectedCode.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleSubmit}
                      disabled={!hasChanges || saving}
                      style={{
                        background: hasChanges && !saving ? '#7c3aed' : '#9ca3af',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                  <div style={{ 
                    padding: '24px',
                    maxHeight: 'calc(100vh - 300px)',
                    overflowY: 'auto'
                  }}>
                    <form onSubmit={handleSubmit}>
                      {/* Two Column Layout */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '24px',
                        marginBottom: '24px'
                      }}>
                        {/* Left Column */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {/* Code */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label htmlFor="edit-code" style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '8px'
                            }}>
                              Code <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              id="edit-code"
                              type="text"
                              name="code"
                              value={formData.code}
                              onChange={handleInputChange}
                              required
                              placeholder="e.g., DRAWNTOGETHER_WEEK1"
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            />
                          </div>

                          {/* Reward Type */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label htmlFor="edit-rewardType" style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '8px'
                            }}>
                              Reward Type <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <select
                              id="edit-rewardType"
                              name="rewardType"
                              value={formData.rewardType}
                              onChange={handleInputChange}
                              required
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box',
                                cursor: 'pointer'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            >
                              <option value="Single Session">Single Session</option>
                              <option value="Multiple Sessions">Multiple Sessions</option>
                              <option value="Unlimited Access">Unlimited Access</option>
                            </select>
                          </div>

                          {/* Max Redemptions */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label htmlFor="edit-maxRedemptions" style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '8px'
                            }}>
                              Max Redemptions
                            </label>
                            <input
                              id="edit-maxRedemptions"
                              type="number"
                              name="maxRedemptions"
                              value={formData.maxRedemptions}
                              onChange={handleInputChange}
                              min="1"
                              placeholder="e.g., 300"
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            />
                          </div>
                        </div>

                        {/* Right Column */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {/* Name */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label htmlFor="edit-name" style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '8px'
                            }}>
                              Name <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              id="edit-name"
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="e.g., Drawn Together Week 1"
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            />
                          </div>

                          {/* Campaign */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label htmlFor="edit-campaign" style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '8px'
                            }}>
                              Campaign
                            </label>
                            <input
                              id="edit-campaign"
                              type="text"
                              name="campaign"
                              value={formData.campaign}
                              onChange={handleInputChange}
                              placeholder="e.g., drawn_together_first_300"
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            />
                          </div>

                          {/* Access Duration */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label htmlFor="edit-accessDuration" style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              marginBottom: '8px'
                            }}>
                              Access Duration (days)
                            </label>
                            <input
                              id="edit-accessDuration"
                              type="number"
                              name="accessDuration"
                              value={formData.accessDuration}
                              onChange={handleInputChange}
                              min="1"
                              placeholder="e.g., 365"
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Description - Full Width */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                        <label htmlFor="edit-description" style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Description
                        </label>
                        <textarea
                          id="edit-description"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="e.g., Unlock one session per week with Drawn Together"
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                      </div>
                    </form>
                  </div>
                </div>
              ) : (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '80px 40px',
                  textAlign: 'center'
                }}>
                  <i className="fas fa-ticket-alt" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
                  <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
                    Select a promo code from the list to start editing
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Add New Promo Code Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowAddModal(false);
            setEditingCode(null);
            resetForm();
          }}
        >
          <Card
            style={{
              maxWidth: '900px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              background: 'white'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Header with Title and Close Button */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#111827',
                margin: 0
              }}>
                Promo Codes
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingCode(null);
                  resetForm();
                }}
                style={{
                  background: 'transparent',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px',
                  fontSize: '18px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <CardContent style={{ padding: '24px' }}>
              <form 
                onSubmit={handleSubmit}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Two Column Layout */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px',
                  marginBottom: '24px'
                }}>
                  {/* Left Column */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Code */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label htmlFor="code" style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Code <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="code"
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., DRAWNTOGETHER_WEEK1"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Reward Type */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label htmlFor="rewardType" style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Reward Type <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        id="rewardType"
                        name="rewardType"
                        value={formData.rewardType}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          boxSizing: 'border-box',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      >
                        <option value="Single Session">Single Session</option>
                        <option value="Multiple Sessions">Multiple Sessions</option>
                        <option value="Unlimited Access">Unlimited Access</option>
                      </select>
                    </div>

                    {/* Max Redemptions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label htmlFor="maxRedemptions" style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Max Redemptions
                      </label>
                      <input
                        id="maxRedemptions"
                        type="number"
                        name="maxRedemptions"
                        value={formData.maxRedemptions}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g., 300"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Name */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label htmlFor="name" style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Name <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Drawn Together Week 1"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Campaign */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label htmlFor="campaign" style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Campaign
                      </label>
                      <input
                        id="campaign"
                        type="text"
                        name="campaign"
                        value={formData.campaign}
                        onChange={handleInputChange}
                        placeholder="e.g., drawn_together_first_300"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>

                    {/* Access Duration */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label htmlFor="accessDuration" style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Access Duration (days)
                      </label>
                      <input
                        id="accessDuration"
                        type="number"
                        name="accessDuration"
                        value={formData.accessDuration}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g., 365"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                  </div>
                </div>

                {/* Description - Full Width */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                  <label htmlFor="description" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g., Unlock one session per week with Drawn Together"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Form Actions - Bottom Right */}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    type="submit"
                    disabled={saving}
                    style={{
                      background: saving ? '#9ca3af' : '#1e40af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.background = '#1e3a8a';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) {
                        e.target.style.background = '#1e40af';
                      }
                    }}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Creating...
                      </>
                    ) : (
                      'Create Promo Code'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </div>
  );
};

export default PromoCode;